import React, { useEffect, useState } from "react";
import { Plan } from "../../../@types";
import { getStarRating } from "../../../helpers/starRatingHelpers";
import { useTranslate } from "../../../helpers/intlHooks";
import { sendTealiumEvent } from "../../../helpers";
import StarRatingRow from "./StarRatingRow";
import StarRatingList from "./StarRatingList";
import StarRatingListItem from "./StarRatingListItem";
import { StarRating } from "../../../components";

const HealthPlanStarRatings: React.FunctionComponent<{
  plan: Plan;
  expandAll?: boolean;
}> = ({ plan, expandAll = false }) => {
  const t = useTranslate();
  const { contract_year } = plan;
  const notCY22 = contract_year !== "2022";
  const [healthPlanExpanded, setHealthPlanExpanded] = useState(expandAll);
  const [stayingHealthyExpanded, setStayingHealthyExpanded] = useState(
    expandAll
  );
  const [managingChronicExpanded, setManagingChronicExpanded] = useState(
    expandAll
  );
  const [memberExperienceExpanded, setMemberExperienceExpanded] = useState(
    expandAll
  );
  const [memberComplaintsExpanded, setMemberComplaintsExpanded] = useState(
    expandAll
  );
  const [
    healthPlanCustomerServiceExpanded,
    setHealthPlanCustomerServiceExpanded,
  ] = useState(expandAll);

  const toggleHealthPlanExpanded = (): void => {
    setHealthPlanExpanded(!healthPlanExpanded);

    if (!healthPlanExpanded) {
      setStayingHealthyExpanded(false);
      setManagingChronicExpanded(false);
      setMemberExperienceExpanded(false);
      setMemberComplaintsExpanded(false);
      setHealthPlanCustomerServiceExpanded(false);

      sendTealiumEvent(
        "Plan Details - Star Ratings",
        "Toggle - Health Plan Star Ratings"
      );
    }
  };

  useEffect(() => {
    setHealthPlanExpanded(expandAll);
    setStayingHealthyExpanded(expandAll);
    setManagingChronicExpanded(expandAll);
    setMemberExperienceExpanded(expandAll);
    setMemberComplaintsExpanded(expandAll);
    setHealthPlanCustomerServiceExpanded(expandAll);
  }, [expandAll]);

  return (
    <div data-testid="health-plan-ratings">
      <StarRatingRow
        label={t("plan_stars.health.star_ratings")}
        open={healthPlanExpanded}
        onClick={toggleHealthPlanExpanded}
        color="green"
        showOriginalMedicareStars
        plan={plan}
      >
        {/* Staying Healthy */}
        <StarRatingRow
          label={t("plan_stars.health.staying_healthy")}
          open={stayingHealthyExpanded}
          starRating={getStarRating(plan, "STAR_STAYING_HEALTHY_RATING")}
          onClick={(): void => {
            setStayingHealthyExpanded(!stayingHealthyExpanded);
            if (!stayingHealthyExpanded) {
              sendTealiumEvent(
                "Plan Details - Star Ratings",
                "Toggle - Helath Plan staying healthy"
              );
            }
          }}
          plan={plan}
        >
          <StarRatingList>
            <StarRatingListItem
              label={t("plan_stars.health.staying_healthy.breast_cancer")}
              starRating={getStarRating(plan, "STAR_BREAST_CANCER_RATING")}
              planYear={plan.contract_year}
            />
            <StarRatingListItem
              label={t("plan_stars.health.staying_healthy.colorectal_cancer")}
              starRating={getStarRating(plan, "STAR_COLORECTAL_CANCER_RATING")}
              planYear={plan.contract_year}
            />
            <StarRatingListItem
              label={t("plan_stars.health.staying_healthy.flu")}
              starRating={getStarRating(plan, "STAR_ANNUAL_FLU_RATING")}
              planYear={plan.contract_year}
            />
            {notCY22 && (
              <StarRatingListItem
                label={t("plan_stars.health.staying_healthy.physical")}
                starRating={getStarRating(plan, "STAR_PHYSICAL_MAINT_RATING")}
                planYear={plan.contract_year}
              />
            )}
            {notCY22 && (
              <StarRatingListItem
                label={t("plan_stars.health.staying_healthy.mental")}
                starRating={getStarRating(plan, "STAR_MENTAL_MAINT_RATING")}
                planYear={plan.contract_year}
              />
            )}
            <StarRatingListItem
              label={t("plan_stars.health.staying_healthy.monitoring_physical")}
              starRating={getStarRating(plan, "STAR_PHYSICAL_ACTIVITY")}
              planYear={plan.contract_year}
            />
            {notCY22 && (
              <StarRatingListItem
                label={t("plan_stars.health.staying_healthy.weight")}
                starRating={getStarRating(plan, "STAR_HEALTHY_WEIGHT_RATING")}
                planYear={plan.contract_year}
              />
            )}
          </StarRatingList>
        </StarRatingRow>

        {/* Chronic conditions */}
        <StarRatingRow
          label={t("plan_stars.health.chronic")}
          open={managingChronicExpanded}
          starRating={getStarRating(plan, "STAR_CHRONIC_CONDITIONS_RATING")}
          onClick={(): void => {
            setManagingChronicExpanded(!managingChronicExpanded);
            if (!managingChronicExpanded) {
              sendTealiumEvent(
                "Plan Details - Star Ratings",
                "Toggle - Helath Plan chronic conditions"
              );
            }
          }}
          plan={plan}
        >
          <StarRatingList>
            <StarRatingListItem
              label={t("plan_stars.health.chronic.needs")}
              starRating={getStarRating(plan, "STAR_HEALTH_ASSESSMENT_RATING")}
              planYear={plan.contract_year}
            />
            <StarRatingListItem
              label={t("plan_stars.health.chronic.drugs")}
              starRating={getStarRating(plan, "STAR_YEARLY_DRUG_REVIEW_RATING")}
              planYear={plan.contract_year}
            />
            {notCY22 && (
              <StarRatingListItem
                label={t("plan_stars.health.chronic.activity")}
                starRating={getStarRating(plan, "STAR_DAILY_ACTIVITIES_RATING")}
                planYear={plan.contract_year}
              />
            )}
            <StarRatingListItem
              label={t("plan_stars.health.chronic.pain")}
              starRating={getStarRating(plan, "STAR_YEARLY_PAIN_SCREEN_RATING")}
              planYear={plan.contract_year}
            />
            <StarRatingListItem
              label={t("plan_stars.health.chronic.os")}
              starRating={getStarRating(plan, "STAR_OSTEOPOROSIS_RATING")}
              planYear={plan.contract_year}
            />
            <StarRatingListItem
              label={t("plan_stars.health.chronic.eye")}
              starRating={getStarRating(plan, "STAR_EYE_EXAM_RATING")}
              planYear={plan.contract_year}
            />
            <StarRatingListItem
              label={t("plan_stars.health.chronic.kidney")}
              starRating={getStarRating(plan, "STAR_KIDNEY_TESTING_RATING")}
              planYear={plan.contract_year}
            />
            <StarRatingListItem
              label={t("plan_stars.health.chronic.sugar")}
              starRating={getStarRating(plan, "STAR_SUGAR_CONTROL_RATING")}
              planYear={plan.contract_year}
            />
            <StarRatingListItem
              label={t("plan_stars.health.chronic.arthritis")}
              starRating={getStarRating(plan, "STAR_ARTHRITIS_RATING")}
              planYear={plan.contract_year}
            />
            <StarRatingListItem
              label={t("plan_stars.health.chronic.falling")}
              starRating={getStarRating(plan, "STAR_FALL_RISK_RATING")}
              planYear={plan.contract_year}
            />
            <StarRatingListItem
              label={t("plan_stars.health.chronic.bladder")}
              starRating={getStarRating(plan, "STAR_BLADDER_CONTROL_RATING")}
              planYear={plan.contract_year}
            />
            <StarRatingListItem
              label={t("plan_stars.health.chronic.uptodate")}
              starRating={getStarRating(plan, "STAR_UPTODATE_RECORDS_RATING")}
              planYear={plan.contract_year}
            />
            {plan.contract_year === "2020" && (
              <StarRatingListItem
                label={t("plan_stars.health.chronic.readmission")}
                starRating={getStarRating(plan, "STAR_READMISSION_RATING")}
                planYear={plan.contract_year}
              />
            )}
            <StarRatingListItem
              label={t("plan_stars.health.chronic.heart")}
              starRating={getStarRating(plan, "STAR_HD_CHOLESTEROL_RATING")}
              planYear={plan.contract_year}
            />
          </StarRatingList>
        </StarRatingRow>

        {/* Member Experience */}
        <StarRatingRow
          label={t("plan_stars.health.experience")}
          open={memberExperienceExpanded}
          starRating={getStarRating(plan, "STAR_MEMBER_HEALTH_EXP_RATING")}
          onClick={(): void => {
            setMemberExperienceExpanded(!memberExperienceExpanded);
            if (!memberExperienceExpanded) {
              sendTealiumEvent(
                "Plan Details - Star Ratings",
                "Toggle - Helath Plan member experience"
              );
            }
          }}
          plan={plan}
        >
          <StarRatingList>
            <StarRatingListItem
              label={t("plan_stars.health.experience.specialists")}
              starRating={getStarRating(plan, "STAR_SEEING_SPECIALISTS_RATING")}
              planYear={plan.contract_year}
            />
            <StarRatingListItem
              label={t("plan_stars.health.experience.appointments")}
              starRating={getStarRating(plan, "STAR_QUICK_APPOINTMENTS_RATING")}
              planYear={plan.contract_year}
            />
            <StarRatingListItem
              label={t("plan_stars.health.experience.information")}
              starRating={getStarRating(plan, "STAR_INFORMATION_RATING")}
              planYear={plan.contract_year}
            />
            <StarRatingListItem
              label={t("plan_stars.health.experience.quality")}
              starRating={getStarRating(plan, "STAR_CARE_QUALITY_RATING")}
              planYear={plan.contract_year}
            />
            <StarRatingListItem
              label={t("plan_stars.health.experience.rating")}
              starRating={getStarRating(plan, "STAR_MEMBER_HEALTH_PLAN_RATING")}
              planYear={plan.contract_year}
            />
            <StarRatingListItem
              label={t("plan_stars.health.experience.coordination")}
              starRating={getStarRating(plan, "STAR_COORDINATION_RATING")}
              planYear={plan.contract_year}
            />
          </StarRatingList>
        </StarRatingRow>

        {/* Member Complaints */}
        <StarRatingRow
          label={t("plan_stars.health.complaints")}
          open={memberComplaintsExpanded}
          starRating={getStarRating(plan, "STAR_HEALTH_COMPLAINTS_RATING")}
          onClick={(): void => {
            setMemberComplaintsExpanded(!memberComplaintsExpanded);
            if (!memberComplaintsExpanded) {
              sendTealiumEvent(
                "Plan Details - Star Ratings",
                "Toggle - Helath Plan member complaints"
              );
            }
          }}
          plan={plan}
        >
          <StarRatingList>
            <StarRatingListItem
              label={t("plan_stars.health.complaints.complaints")}
              starRating={getStarRating(
                plan,
                "STAR_HEALTH_PLAN_COMPLAINTS_RATING"
              )}
              planYear={plan.contract_year}
            />
            <StarRatingListItem
              label={t("plan_stars.health.complaints.leaving")}
              starRating={getStarRating(plan, "STAR_HEALTH_LEAVING_RATING")}
              planYear={plan.contract_year}
            />
            <StarRatingListItem
              label={t("plan_stars.health.complaints.improvement")}
              starRating={getStarRating(plan, "STAR_HEALTH_IMPROVEMENT_RATING")}
              planYear={plan.contract_year}
            />
          </StarRatingList>
        </StarRatingRow>

        {/* Customer Service */}
        <StarRatingRow
          label={t("plan_stars.health.customer_service")}
          open={healthPlanCustomerServiceExpanded}
          starRating={getStarRating(plan, "STAR_HEALTH_SERVICE_RATING")}
          onClick={(): void => {
            setHealthPlanCustomerServiceExpanded(
              !healthPlanCustomerServiceExpanded
            );
            if (!healthPlanCustomerServiceExpanded) {
              sendTealiumEvent(
                "Plan Details - Star Ratings",
                "Toggle - Helath Plan customer service"
              );
            }
          }}
          plan={plan}
        >
          <StarRatingList>
            <StarRatingListItem
              label={t("plan_stars.health.customer_service.timely_decisions")}
              starRating={getStarRating(plan, "STAR_HEALTH_APPEALS_RATING")}
              planYear={plan.contract_year}
            />
            <StarRatingListItem
              label={t("plan_stars.health.customer_service.fair_appeals")}
              starRating={getStarRating(
                plan,
                "STAR_HEALTH_APPEALS_FAIR_RATING"
              )}
              planYear={plan.contract_year}
            />
            <StarRatingListItem
              label={t("plan_stars.health.customer_service.tty")}
              starRating={getStarRating(plan, "STAR_HEALTH_TTY_AVAIL_RATING")}
              planYear={plan.contract_year}
            />
          </StarRatingList>
        </StarRatingRow>
      </StarRatingRow>

      {/* Summary */}
      <div className={`StarRatingRow StarRatingRow__blue`}>
        <h3 className={"StarRatingsSection__heading"}>
          {t("plan_stars.health.summary")}
        </h3>
        <StarRating
          stars={getStarRating(plan, "STAR_HEALTH_PLAN_RATING")}
          includeLabel={false}
          planYear={plan.contract_year}
        />
      </div>
    </div>
  );
};

export default HealthPlanStarRatings;
